/* Banners change in place. Touch visitors choose a banner with the buttons. */
(() => {
  if (window.XEngineersBannerCarouselLoaded) return;
  window.XEngineersBannerCarouselLoaded = true;

  class XEngineersBannerCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.viewport = this.querySelector('[data-xe-carousel-viewport]');
      this.slides = [...this.querySelectorAll('[data-xe-carousel-slide]')];
      if (!this.viewport || !this.slides.length) return;
      this.initialized = true;
      this.dots = [...this.querySelectorAll('[data-xe-carousel-dot]')];
      this.toggleButton = this.querySelector('[data-xe-carousel-toggle]');
      this.status = this.querySelector('[data-xe-carousel-status]');
      this.currentIndex = Math.min(this.currentIndex || 0, this.slides.length - 1);
      this.timer = null;
      this.userPaused = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.editorPaused = false;
      this.visible = true;
      this.interval = Math.max(Number(this.dataset.autoplaySpeed) || 5000, 3000);
      this.coarse = window.matchMedia('(pointer: coarse)');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.listeners = [];
      const listen = (target, type, callback) => {
        if (!target) return;
        target.addEventListener(type, callback);
        this.listeners.push(() => target.removeEventListener(type, callback));
      };

      listen(this.viewport, 'keydown', event => {
        if (this.slides.length < 2 || event.target.closest('input, select, textarea, [contenteditable]')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1), true);
        }
      });
      this.dots.forEach((dot, fallbackIndex) => listen(dot, 'click', () => {
        const index = Number(dot.dataset.xeCarouselDot);
        this.goTo(Number.isInteger(index) ? index : fallbackIndex, true);
      }));
      listen(this.querySelector('[data-xe-carousel-previous]'), 'click', () => this.goTo(this.currentIndex - 1, true));
      listen(this.querySelector('[data-xe-carousel-next]'), 'click', () => this.goTo(this.currentIndex + 1, true));
      listen(this.toggleButton, 'click', () => {
        this.userPaused = !this.userPaused;
        this.syncAutoplay(true);
      });
      listen(this, 'mouseenter', () => {
        this.hoverPaused = this.dataset.pauseOnHover === 'true';
        this.syncAutoplay();
      });
      listen(this, 'mouseleave', () => { this.hoverPaused = false; this.syncAutoplay(true); });
      listen(this, 'focusin', event => {
        this.focusPaused = Boolean(event.target.closest('a, button, input, select, textarea, [contenteditable]'));
        this.syncAutoplay();
      });
      listen(this, 'focusout', event => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.syncAutoplay(true);
      });
      listen(document, 'visibilitychange', () => this.syncAutoplay(true));
      listen(this.coarse, 'change', () => this.syncAutoplay(true));
      listen(this.motion, 'change', () => this.syncAutoplay(true));
      listen(document, 'shopify:block:select', event => {
        const slide = event.target.closest('[data-xe-carousel-slide]');
        if (!slide || !this.contains(slide)) return;
        this.editorPaused = true;
        this.goTo(this.slides.indexOf(slide), true);
      });
      listen(document, 'shopify:block:deselect', event => {
        if (!this.contains(event.target)) return;
        this.editorPaused = false;
        this.syncAutoplay(true);
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0]?.isIntersecting ?? true;
          this.syncAutoplay(true);
        }, { threshold: .05 });
        this.observer.observe(this);
      }
      if (this.slides.length < 2) this.viewport.removeAttribute('tabindex');
      this.updateState(false);
      this.syncAutoplay();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.observer?.disconnect();
      this.listeners?.forEach(remove => remove());
      this.initialized = false;
    }

    goTo(index, announce = false, restart = true) {
      if (this.slides.length < 2) return;
      this.currentIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      this.updateState(announce);
      if (restart) this.syncAutoplay(true);
    }

    updateState(announce) {
      this.slides.forEach((slide, index) => {
        const active = index === this.currentIndex;
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('inert', !active);
      });
      this.dots.forEach((dot, fallbackIndex) => {
        const index = Number(dot.dataset.xeCarouselDot);
        dot.setAttribute('aria-current', String((Number.isInteger(index) ? index : fallbackIndex) === this.currentIndex));
      });
      if (announce && this.status) this.status.textContent = `Banner ${this.currentIndex + 1} of ${this.slides.length}`;
      this.dataset.activeSlide = String(this.currentIndex + 1);
    }

    canAutoplay() {
      return this.dataset.autoplay === 'true' && !this.coarse.matches && !this.motion.matches &&
        this.slides.length > 1 && !this.userPaused && !this.hoverPaused && !this.focusPaused &&
        !this.editorPaused && this.visible && !document.hidden;
    }

    syncAutoplay(reset = false) {
      const playing = this.canAutoplay();
      if (reset || !playing) { window.clearTimeout(this.timer); this.timer = null; }
      if (playing && !this.timer) {
        this.timer = window.setTimeout(() => {
          this.timer = null;
          this.goTo(this.currentIndex + 1, false, false);
          this.syncAutoplay();
        }, this.interval);
      }
      this.dataset.autoplayState = playing ? 'playing' : 'paused';
      if (!this.toggleButton) return;
      this.toggleButton.hidden = this.dataset.autoplay !== 'true' || this.coarse.matches || this.motion.matches || this.slides.length < 2;
      this.toggleButton.setAttribute('aria-label', this.userPaused ? 'Play automatic banners' : 'Pause automatic banners');
      this.toggleButton.setAttribute('aria-pressed', String(this.userPaused));
      const icon = this.toggleButton.querySelector('span');
      if (icon) icon.textContent = this.userPaused ? '▶' : 'Ⅱ';
    }
  }
  if (!customElements.get('xengineers-banner-carousel')) customElements.define('xengineers-banner-carousel', XEngineersBannerCarousel);
})();
